<script setup>
import { createEvent } from 'ics'
import RightArrow from '@/assets/img/ico/right-arrow.svg'
import CurvedArrow from '@/assets/img/ico/curved-right-arrow.svg'
import PdfArrow from '@/assets/img/ico/pdf-arrow.svg'
import Asterix from '@/assets/img/ico/asterix.svg'

const props = defineProps({
  text: {
    type: String,
    default: '',
  },
  content: {
    type: String,
    default: '',
  },
  dataEvento: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: 'right-arrow',
  },
})

const title = 'Open Day per le famiglie | Istituto Canossiano'
const location = 'Piazza Cesare Battisti 27, Conselve'

const isExternal = computed(() => /^https?:\/\//.test(props.content))

const iconComponent = computed(() => {
  if (props.content.includes('.pdf') || props.content.includes('.docx')) {
    return PdfArrow
  }
  if (props.dataEvento) {
    return Asterix
  }
  switch (props.icon) {
    case 'right-arrow':
      return RightArrow
    case 'curved-arrow':
      return CurvedArrow
    default:
      return null
  }
})

function isAndroidDevice() {
  // Simple user agent check for Android devices
  return /android/i.test(navigator.userAgent)
}

function formatDateForGoogle(date) {
  const pad = (n) => (n < 10 ? '0' + n : n) // Padding for single digit numbers
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate()) + 'T'}${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds()) + 'Z'}`
}

function createGoogleCalendarLink(
  startDate,
  endDate,
  topic,
  location,
  description = '',
) {
  const formattedStartDate = formatDateForGoogle(new Date(startDate))
  const formattedEndDate = formatDateForGoogle(new Date(endDate))

  // Construct the Google Calendar event URL
  const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(topic)}&dates=${formattedStartDate}/${formattedEndDate}&details=${encodeURIComponent(description)}&location=${encodeURIComponent(location)}`
  return googleCalendarUrl
}

function createICSFile(startDate, endDate, topic, location, description = '') {
  // Function to format the date into iCal format (UTC, with Z)
  function formatDateToICS(date) {
    const pad = (n) => (n < 10 ? '0' + n : n) // Padding for single digit numbers
    return (
      date.getUTCFullYear() +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) +
      'T' +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      pad(date.getUTCSeconds()) +
      'Z'
    )
  }

  // Formatting dates to UTC format for the iCal file
  const formattedStartDate = formatDateToICS(new Date(startDate))
  const formattedEndDate = formatDateToICS(new Date(endDate))

  // iCalendar file content
  const icsContent = `
BEGIN:VCALENDAR
VERSION:2.0
CALSCALE:GREGORIAN
METHOD:PUBLISH
PRODID:-//YourApp//EN
BEGIN:VEVENT
UID:${new Date().getTime()}@yourapp.com
DTSTAMP:${formatDateToICS(new Date())}
DTSTART:${formattedStartDate}
DTEND:${formattedEndDate}
SUMMARY:${topic}
LOCATION:${location}
DESCRIPTION:${description}
STATUS:CONFIRMED
TRANSP:OPAQUE
SEQUENCE:0
END:VEVENT
END:VCALENDAR
    `.trim() // Trim to remove any accidental extra spaces or lines

  // Create a Blob object with the .ics file content
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' })
  const fileName = `${topic.replace(/\s+/g, '_')}.ics`

  // Check if the device supports download via Blob
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    // For older browsers (mainly IE and Edge)
    window.navigator.msSaveOrOpenBlob(blob, fileName)
  } else {
    const url = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    downloadLink.download = fileName

    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)

    // Clean up URL object
    setTimeout(() => URL.revokeObjectURL(url), 100)
  }
}

const addToCalendar = () => {
  const dateParts = props.dataEvento.split('-')
  if (dateParts.length !== 3) {
    console.error('Errore nella Data: formato non valido')
    return
  }

  const [year, month, day] = dateParts.map(Number)

  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    console.error('Errore nella Data: valori non validi')
    return
  }

  const event = {
    start: [year, month, day, 9, 0], // Ora inizio: 09:00
    duration: { hours: 2.5 },
    title,
    location,
  }

  // Ok settiamo ora inizio e fine harcoded per il Google Calendar (09/11.30)
  const eventStartDate = new Date(props.dataEvento)
  const eventEndDate = new Date(props.dataEvento)
  eventStartDate.setHours(9)
  eventEndDate.setHours(11)
  eventEndDate.setMinutes(30)
  const isoStart = eventStartDate.toISOString()
  const isoEnd = eventEndDate.toISOString()

  if (isAndroidDevice()) {
    // Redirect Android users to Google Calendar
    const googleCalendarLink = createGoogleCalendarLink(
      isoStart,
      isoEnd,
      title,
      location,
    )
    window.open(googleCalendarLink, '_blank')
  } else {
    createEvent(event, (error, value) => {
      if (error) {
        console.log(error)
        return
      }
      const blob = new Blob([value], { type: 'text/calendar' })
      const reader = new FileReader()
      reader.onload = function (event) {
        const link = document.createElement('a')
        link.href = event.target.result
        link.download = 'open-day-istituto-canossiano.ics'
        link.click()
      }
      reader.readAsDataURL(blob)
    })
  }
}

const handleClick = (event) => {
  if (props.dataEvento) {
    event.preventDefault()
    addToCalendar()
  }
}
</script>

<template>
  <div class="default-btn">
    <a
      :href="props.content"
      :target="isExternal ? '_blank' : null"
      class="text-xs-1"
      @click="handleClick"
    >
      <span>{{ props.text }}</span>
      <iconComponent class="arrow-icon" :filled="true" />
    </a>
  </div>
</template>
<script>
export default {
  name: 'DefaultBtn',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
